<template>
  <div class="data-query">
    <div class="page-header">
      <h2>数据查询与报表</h2>
      <p>汇总支付、退款、免费单和风险提示</p>
    </div>

    <div class="page-content">
      <div class="toolbar">
        <div class="field-group">
          <label>开始时间</label>
          <input v-model="startTime" class="datetime-input" type="datetime-local" />
        </div>
        <div class="field-group">
          <label>结束时间</label>
          <input v-model="endTime" class="datetime-input" type="datetime-local" />
        </div>
        <button class="primary-btn" :disabled="loading" @click="loadDashboard">
          {{ loading ? '加载中...' : '刷新看板' }}
        </button>
      </div>

      <div class="card-grid">
        <div class="overview-card">
          <span class="card-label">流水总额</span>
          <strong class="card-value">{{ formatMoney(totalPayment) }}元</strong>
          <span class="card-sub">当前筛选时段全部有效流水</span>
        </div>
        <div class="overview-card success-card">
          <span class="card-label">净营业额</span>
          <strong class="card-value">{{ formatMoney(businessSummary.netRevenueAmount) }}元</strong>
          <span class="card-sub">会员余额扣费不计入营业额</span>
        </div>
        <div class="overview-card warning-card">
          <span class="card-label">退款金额</span>
          <strong class="card-value">{{ formatMoney(businessSummary.refundAmount) }}元</strong>
          <span class="card-sub">退款笔数 {{ businessSummary.refundCount || 0 }}</span>
        </div>
        <div class="overview-card">
          <span class="card-label">会员余额流水</span>
          <strong class="card-value">{{ formatMoney(businessSummary.memberBalanceFlowAmount) }}元</strong>
          <span class="card-sub">仅算流水，不算营收</span>
        </div>
        <div class="overview-card success-card">
          <span class="card-label">免费单/撤销</span>
          <strong class="card-value">{{ businessSummary.freeOrderCount || 0 }} / {{ businessSummary.revokedOrderCount || 0 }}</strong>
          <span class="card-sub">免费金额 {{ formatMoney(businessSummary.freeOrderAmount) }}元</span>
        </div>
        <div class="overview-card danger-card">
          <span class="card-label">风险提示</span>
          <strong class="card-value">{{ riskHints.length }}</strong>
          <span class="card-sub">自动识别退款/免费异常</span>
        </div>
        <div class="overview-card">
          <span class="card-label">设备开机总时长</span>
          <strong class="card-value">{{ formatDurationMinutes(deviceUsageSummary.totalUsageMinutes) }}</strong>
          <span class="card-sub">计时设备 {{ deviceUsageSummary.timedSessions || 0 }} 次</span>
        </div>
      </div>

      <div class="dashboard-grid">
        <section class="panel">
          <div class="panel-header">
            <h3>支付方式统计</h3>
            <span>共 {{ paymentStats.length }} 项</span>
          </div>
          <table class="stats-table">
            <thead>
              <tr>
                <th>支付方式</th>
                <th class="align-right">流水</th>
                <th class="align-right">净营业额</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="3" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="paymentStats.length === 0">
                <td colspan="3" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-for="item in paymentStats" :key="item.payTypeName">
                <td>{{ item.payTypeName }}</td>
                <td class="align-right">{{ formatMoney(item.amount) }}元</td>
                <td class="align-right">{{ formatMoney(item.revenueAmount) }}元</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="panel">
          <div class="panel-header">
            <h3>业务汇总</h3>
            <span>退款与免费单</span>
          </div>
          <div class="summary-list">
            <div class="summary-item">
              <span>流水总额</span>
              <strong>{{ formatMoney(businessSummary.totalFlowAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>毛营业额</span>
              <strong>{{ formatMoney(businessSummary.grossRevenueAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>净营业额</span>
              <strong>{{ formatMoney(businessSummary.netRevenueAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>退款金额</span>
              <strong>{{ formatMoney(businessSummary.refundAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>会员余额流水</span>
              <strong>{{ formatMoney(businessSummary.memberBalanceFlowAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>退款笔数</span>
              <strong>{{ businessSummary.refundCount || 0 }}</strong>
            </div>
            <div class="summary-item">
              <span>免费单笔数</span>
              <strong>{{ businessSummary.freeOrderCount || 0 }}</strong>
            </div>
            <div class="summary-item">
              <span>免费单金额</span>
              <strong>{{ formatMoney(businessSummary.freeOrderAmount) }}元</strong>
            </div>
            <div class="summary-item">
              <span>撤销订单</span>
              <strong>{{ businessSummary.revokedOrderCount || 0 }}</strong>
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-header">
            <h3>设备开机时长统计</h3>
            <span>当前时段设备使用概况</span>
          </div>
          <div class="summary-list">
            <div class="summary-item">
              <span>开机次数</span>
              <strong>{{ deviceUsageSummary.totalSessions || 0 }}</strong>
            </div>
            <div class="summary-item">
              <span>计时设备次数</span>
              <strong>{{ deviceUsageSummary.timedSessions || 0 }}</strong>
            </div>
            <div class="summary-item">
              <span>直接扣费次数</span>
              <strong>{{ deviceUsageSummary.directChargeSessions || 0 }}</strong>
            </div>
            <div class="summary-item">
              <span>累计开机时长</span>
              <strong>{{ formatDurationMinutes(deviceUsageSummary.totalUsageMinutes) }}</strong>
            </div>
          </div>
        </section>

        <section class="panel panel-wide">
          <div class="panel-header">
            <h3>风险提示</h3>
            <span>{{ riskHints.length ? '按当前时段自动生成' : '当前时段暂无明显异常' }}</span>
          </div>
          <div v-if="loading" class="empty-state">加载中...</div>
          <div v-else-if="riskHints.length === 0" class="empty-state">当前时段暂无风险提示</div>
          <div v-else class="risk-list">
            <div v-for="item in riskHints" :key="item.code + item.subject" :class="['risk-item', item.level || 'low']">
              <div class="risk-top">
                <strong>{{ item.title }}</strong>
                <span class="risk-tag">{{ getLevelLabel(item.level) }}</span>
              </div>
              <div class="risk-subject" v-if="item.subject">{{ item.subject }}</div>
              <p class="risk-desc">{{ item.description }}</p>
              <div class="risk-meta">
                <span>次数 {{ item.count || 0 }}</span>
                <span>金额 {{ formatMoney(item.amount) }}元</span>
              </div>
            </div>
          </div>
        </section>

        <section class="panel panel-wide">
          <div class="panel-header">
            <h3>{{ currentYear }} 年门店业绩概览</h3>
            <button class="text-btn" :disabled="monthlyLoading" @click="loadMonthlyStats">
              {{ monthlyLoading ? '加载中...' : '刷新年度概览' }}
            </button>
          </div>
          <table class="stats-table">
            <thead>
              <tr>
                <th>门店</th>
                <th>门店编码</th>
                <th class="align-right">年度总计</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="monthlyLoading">
                <td colspan="3" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="topMonthlyStores.length === 0">
                <td colspan="3" class="empty-cell">暂无年度业绩数据</td>
              </tr>
              <tr v-for="item in topMonthlyStores" :key="item.storeId">
                <td>{{ item.storeName }}</td>
                <td>{{ item.storeCode || '-' }}</td>
                <td class="align-right">{{ formatMoney(item.totalAmount) }}元</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'DataQuery',
  data() {
    const formatDateTimeLocal = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hour}:${minute}`
    }
    const now = new Date()
    const end = formatDateTimeLocal(now)
    const start = formatDateTimeLocal(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000))
    return {
      startTime: start,
      endTime: end,
      loading: false,
      monthlyLoading: false,
      paymentStats: [],
      riskHints: [],
      monthlyStats: [],
      deviceUsageSummary: {
        totalSessions: 0,
        timedSessions: 0,
        directChargeSessions: 0,
        totalUsageMinutes: 0
      },
      businessSummary: {
        totalFlowAmount: 0,
        grossRevenueAmount: 0,
        netRevenueAmount: 0,
        memberBalanceFlowAmount: 0,
        refundAmount: 0,
        refundCount: 0,
        revokedOrderCount: 0,
        freeOrderCount: 0,
        freeOrderAmount: 0
      }
    }
  },
  computed: {
    totalPayment() {
      return this.paymentStats.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    },
    currentYear() {
      return new Date().getFullYear()
    },
    topMonthlyStores() {
      return [...this.monthlyStats]
        .map(item => ({
          ...item,
          totalAmount: Number(item.totalAmount || 0)
        }))
        .sort((a, b) => b.totalAmount - a.totalAmount)
        .slice(0, 5)
    }
  },
  mounted() {
    this.loadDashboard()
    this.loadMonthlyStats()
  },
  methods: {
    formatDateTimeLocal(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hour}:${minute}`
    },
    buildApiTime(value) {
      return value ? `${value}:00` : ''
    },
    async loadDashboard() {
      if (!this.startTime || !this.endTime || this.startTime >= this.endTime) {
        alert('请选择正确的时间范围')
        return
      }

      this.loading = true
      try {
        const params = {
          startTime: this.buildApiTime(this.startTime),
          endTime: this.buildApiTime(this.endTime)
        }
        const [statsRes, summaryRes, riskRes, deviceUsageRes] = await Promise.all([
          request.get('/api/consumption/pay-type-stats', { params }),
          request.get('/api/consumption/business-summary', { params }),
          request.get('/api/consumption/risk-hints', { params }),
          request.get('/api/consumption/device-usage-summary', { params })
        ])

        this.paymentStats = statsRes.data?.data || []
        this.businessSummary = summaryRes.data?.data || this.businessSummary
        this.riskHints = riskRes.data?.data || []
        this.deviceUsageSummary = deviceUsageRes.data?.data || this.deviceUsageSummary
      } catch (error) {
        console.error('加载报表首页失败:', error)
        this.paymentStats = []
        this.riskHints = []
        this.businessSummary = {
          totalFlowAmount: 0,
          grossRevenueAmount: 0,
          netRevenueAmount: 0,
          memberBalanceFlowAmount: 0,
          refundAmount: 0,
          refundCount: 0,
          revokedOrderCount: 0,
          freeOrderCount: 0,
          freeOrderAmount: 0
        }
      } finally {
        this.loading = false
      }
    },
    async loadMonthlyStats() {
      this.monthlyLoading = true
      try {
        const response = await request.get('/api/consumption/monthlyStats', {
          params: {
            year: this.currentYear
          }
        })
        this.monthlyStats = response.data?.data || []
      } catch (error) {
        console.error('加载年度业绩概览失败:', error)
        this.monthlyStats = []
      } finally {
        this.monthlyLoading = false
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatDurationMinutes(value) {
      const totalMinutes = Number(value || 0)
      if (!totalMinutes) {
        return '0分钟'
      }
      return `${totalMinutes}分钟`
    },
    getLevelLabel(level) {
      if (level === 'high') return '高风险'
      if (level === 'medium') return '中风险'
      return '低风险'
    }
  }
}
</script>

<style scoped>
.data-query {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px;
}

.page-header p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.page-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.toolbar {
  display: flex;
  align-items: end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-group label {
  font-size: 13px;
  color: #666;
}

.datetime-input {
  width: 220px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
}

.primary-btn,
.text-btn {
  height: 38px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.primary-btn {
  padding: 0 18px;
  background: #1677ff;
  color: #fff;
}

.primary-btn:disabled,
.text-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.text-btn {
  padding: 0 10px;
  background: #eff6ff;
  color: #1677ff;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.overview-card {
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.warning-card {
  background: linear-gradient(135deg, #fff7ed, #ffffff);
}

.success-card {
  background: linear-gradient(135deg, #ecfdf5, #ffffff);
}

.danger-card {
  background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.card-label {
  font-size: 13px;
  color: #64748b;
}

.card-value {
  font-size: 28px;
  color: #0f172a;
}

.card-sub {
  font-size: 13px;
  color: #94a3b8;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.panel {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 18px;
}

.panel-wide {
  grid-column: span 2;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1f2937;
}

.panel-header span {
  color: #64748b;
  font-size: 13px;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table th,
.stats-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  font-size: 14px;
}

.align-right {
  text-align: right !important;
}

.empty-cell,
.empty-state {
  text-align: center;
  color: #94a3b8;
  padding: 24px;
}

.summary-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.summary-item {
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-item span {
  color: #64748b;
  font-size: 13px;
}

.summary-item strong {
  color: #0f172a;
  font-size: 22px;
}

.risk-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.risk-item {
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.risk-item.low {
  background: #f8fafc;
}

.risk-item.medium {
  background: #fff7ed;
  border-color: #fdba74;
}

.risk-item.high {
  background: #fef2f2;
  border-color: #fca5a5;
}

.risk-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.risk-top strong {
  color: #0f172a;
}

.risk-tag {
  font-size: 12px;
  color: #475569;
  background: rgba(255, 255, 255, 0.7);
  padding: 2px 8px;
  border-radius: 999px;
}

.risk-subject {
  font-size: 13px;
  color: #1677ff;
  margin-bottom: 6px;
}

.risk-desc {
  margin: 0 0 10px;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
}

.risk-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #64748b;
}

@media (max-width: 1200px) {
  .card-grid,
  .dashboard-grid,
  .risk-list {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 900px) {
  .card-grid,
  .dashboard-grid,
  .risk-list,
  .summary-list {
    grid-template-columns: 1fr;
  }

  .panel-wide {
    grid-column: span 1;
  }
}
</style>
